import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Text,
  DefaultButton,
  PrimaryButton,
  IconButton,
  DetailsList,
  IColumn,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  DetailsListLayoutMode,
  SelectionMode,
  SearchBox,
  IContextualMenuProps,
  Dialog,
  DialogType,
  DialogFooter,
  mergeStyleSets,
} from "@fluentui/react";

import { accessService } from "../services/accessService";
import EditAccessPanel from "./EditAccessPanel";

type DomainCatalogEntry = {
  domain: string;
  display_name: string;
  has_chat: boolean;
  has_dashboard: boolean;
  has_build: boolean;
  has_dataset: boolean;
  has_geospatial: boolean;
  is_sensitive: boolean;
  sort_order: number;
};

type AccessUser = {
  upn: string;
  entity_type?: "user" | "group";
  display_name?: string | null;
  is_app_admin: boolean;
  surfaces: { domain: string; surface: string }[];
  finance_full_access: boolean;
  finance_directorates: string[];
  finance_departments: string[];
  monthly_limit_dollars?: number | string | null;
};

type StaleGrants = {
  directorates: { upn: string; directorate_name: string }[];
  departments: { upn: string; department_name: string }[];
};

const styles = mergeStyleSets({
  card: {
    background: "#FFF",
    border: "1px solid #EAEAEA",
    borderRadius: 10,
    padding: 24,
    marginBottom: 16,
    minWidth: 0,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 1px 3px rgba(15,23,42,.06)",
  },
  tableWrap: {
    marginTop: 12,
    border: "1px solid #EDEDED",
    borderRadius: 6,
    overflowX: "auto",
    background: "#FFFFFF",
    WebkitOverflowScrolling: "touch",
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: "#161617",
    letterSpacing: "-0.01em",
    marginBottom: 4,
  },
  cardSub: {
    fontSize: 12,
    color: "#6B6B73",
    marginBottom: 12,
    maxWidth: "72ch",
  },
  counterStrip: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 0,
    fontSize: 12,
    color: "#6B6B73",
    fontVariantNumeric: "tabular-nums",
    marginTop: 8,
  } as any,
  counterStripItem: {
    selectors: {
      "&:not(:first-child)::before": {
        content: '"·"',
        padding: "0 8px",
        color: "#D4D4D8",
      },
    },
  } as any,
  counterStripValue: {
    color: "#161617",
    fontWeight: 600,
  },
  banner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    background: "#FFF8E1",
    border: "1px solid #F0C464",
    color: "#6B4D00",
    fontSize: 12,
    borderRadius: 6,
    marginBottom: 12,
  } as any,
  bannerSpacer: { flex: 1 },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 12,
    marginBottom: 12,
  } as any,
  toolbarSpacer: { flex: 1 },
  pillGroup: {
    display: "inline-flex",
    gap: 2,
    padding: 3,
    background: "#F7F7F9",
    border: "1px solid #EAEAEA",
    borderRadius: 999,
  } as any,
  filterPill: {
    height: 26,
    padding: "0 11px",
    border: "none",
    background: "transparent",
    color: "#404045",
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 999,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontVariantNumeric: "tabular-nums",
    selectors: {
      "&:hover": { color: "#161617", background: "rgba(0,0,0,.025)" },
    },
  } as any,
  filterPillActive: {
    background: "#FFFFFF",
    color: "#16531F",
    boxShadow: "0 1px 2px rgba(15,23,42,.06), 0 0 0 1px #EAEAEA",
  },
  filterPillCount: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: 10,
    padding: "1px 5px",
    background: "rgba(0,0,0,.05)",
    borderRadius: 8,
    color: "inherit",
    fontWeight: 500,
  } as any,
  filterPillCountActive: { background: "#D9F4D7", color: "#16531F" },
  identityCell: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  } as any,
  identityName: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontWeight: 500,
    color: "#161617",
    fontSize: 13,
  } as any,
  identitySub: {
    fontSize: 11,
    color: "#6B6B73",
    fontFamily: "'Roboto Mono', monospace",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as any,
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "1px 7px",
    height: 17,
    fontSize: 9.5,
    fontWeight: 600,
    letterSpacing: "0.06em",
    borderRadius: 999,
    textTransform: "uppercase",
  } as any,
  chipAdmin: { background: "#D9F4D7", color: "#0D3A1C", boxShadow: "inset 0 0 0 1px rgba(46,135,61,.18)" },
  chipGroup: { background: "#FFF4D9", color: "#6B4D00", boxShadow: "inset 0 0 0 1px rgba(217,154,24,.18)" },
  chipUser:  { background: "#EEF0F2", color: "#3A4151", boxShadow: "inset 0 0 0 1px rgba(58,65,81,.10)" },
  modeChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 9px",
    height: 22,
    fontSize: 11,
    fontWeight: 500,
    borderRadius: 999,
    fontVariantNumeric: "tabular-nums",
  } as any,
  secondaryLine: {
    fontSize: 11,
    color: "#6B6B73",
    marginTop: 3,
    fontVariantNumeric: "tabular-nums",
  } as any,
  rowActions: {
    display: "inline-flex",
    gap: 2,
  } as any,
  budgetMono: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: 12,
    fontWeight: 500,
    color: "#161617",
    fontVariantNumeric: "tabular-nums",
  } as any,
  budgetUnlimited: { color: "#9A9AA3", fontStyle: "italic" },
  pill: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500,
    marginRight: 4,
    marginBottom: 2,
    background: "#F0F0F0",
    color: "#404040",
  },
  pillFull: { background: "#E2F4E5", color: "#1a5c26" },
  pillRestricted: { background: "#FFF4D9", color: "#7A5800" },
  pillNone: { background: "#F0F0F0", color: "#6B6B6B" },
  pillAdmin: { background: "#E2F4E5", color: "#1a5c26" },
  muted: { color: "#6B6B6B", fontSize: 12 },
  detailsCell: { whiteSpace: "normal", wordWrap: "break-word", fontSize: 12 },
});

function financeMode(u: AccessUser): "full" | "restricted" | "none" {
  if (u.finance_full_access) return "full";
  if (u.finance_directorates.length || u.finance_departments.length) return "restricted";
  return "none";
}

const AdminAccessTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [domainCatalog, setDomainCatalog] = useState<DomainCatalogEntry[]>([]);
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [stale, setStale] = useState<StaleGrants>({ directorates: [], departments: [] });
  const [staleAvailable, setStaleAvailable] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<"add" | "addGroup" | "addAdmin" | "edit">("edit");
  const [panelUser, setPanelUser] = useState<AccessUser | null>(null);
  // PR2: cohort + search filter the unified table (admins folded in)
  const [cohort, setCohort] = useState<"all" | "user" | "admin" | "group">("all");
  const [searchTerm, setSearchTerm] = useState("");
  // PR4: Remove-admin confirmation. Replaces window.confirm with a Fluent
  // Dialog so the destructive action gets the same chrome as the rest of
  // the portal — and so the message can name the user explicitly.
  const [removeAdminTarget, setRemoveAdminTarget] = useState<AccessUser | null>(null);
  const [removeAdminBusy, setRemoveAdminBusy] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cat, us] = await Promise.all([
        accessService.get<DomainCatalogEntry[]>("/admin/access/domain-catalog"),
        accessService.get<AccessUser[]>("/admin/access/users"),
      ]);
      setDomainCatalog(cat.data);
      setUsers(us.data);
      // Stale-grants needs Fabric; fail-soft if Fabric is unavailable
      try {
        const st = await accessService.get<StaleGrants>("/admin/access/stale-grants");
        setStale(st.data);
        setStaleAvailable(true);
      } catch {
        setStaleAvailable(false);
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })
        ?.response?.data?.error
        ?? (err as { message?: string })?.message
        ?? "Failed to load access data";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openEdit = useCallback((u: AccessUser) => {
    setPanelUser(u);
    setPanelMode("edit");
    setPanelOpen(true);
  }, []);

  const openAdd = useCallback(() => {
    setPanelUser(null);
    setPanelMode("add");
    setPanelOpen(true);
  }, []);

  const openAddGroup = useCallback(() => {
    setPanelUser(null);
    setPanelMode("addGroup");
    setPanelOpen(true);
  }, []);

  const openAddAdmin = useCallback(() => {
    setPanelUser(null);
    setPanelMode("addAdmin");
    setPanelOpen(true);
  }, []);

  // Open the confirmation dialog. The actual DELETE happens in
  // confirmRemoveAdmin once the operator confirms.
  const askRemoveAdmin = useCallback((user: AccessUser) => {
    setRemoveAdminTarget(user);
  }, []);

  const confirmRemoveAdmin = useCallback(async () => {
    if (!removeAdminTarget) return;
    setRemoveAdminBusy(true);
    try {
      await accessService.delete(`/admin/access/admins/${encodeURIComponent(removeAdminTarget.upn)}`);
      setRemoveAdminTarget(null);
      await loadAll();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? (err as { message?: string })?.message
        ?? "Failed to remove admin";
      setError(msg);
      setRemoveAdminTarget(null);
    } finally {
      setRemoveAdminBusy(false);
    }
  }, [loadAll, removeAdminTarget]);

  // PR2: filtered users — admins folded in; cohort filter pills + search apply.
  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return users.filter((u) => {
      if (cohort === "user"  && (u.is_app_admin || u.entity_type === "group")) return false;
      if (cohort === "admin" && !u.is_app_admin) return false;
      if (cohort === "group" && u.entity_type !== "group") return false;
      if (q) {
        const hay = `${u.upn} ${u.display_name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, cohort, searchTerm]);

  const cohortCounts = useMemo(() => ({
    all: users.length,
    user: users.filter((u) => !u.is_app_admin && u.entity_type !== "group").length,
    admin: users.filter((u) => u.is_app_admin).length,
    group: users.filter((u) => u.entity_type === "group").length,
  }), [users]);

  // Row overflow menu — Edit / Copy UPN / Remove admin (only for admins).
  const rowMenu = useCallback((item: AccessUser): IContextualMenuProps => ({
    items: [
      {
        key: "edit", text: "Edit access", iconProps: { iconName: "Edit" },
        onClick: () => { openEdit(item); return true; },
      },
      {
        key: "copy", text: "Copy UPN", iconProps: { iconName: "Copy" },
        onClick: () => {
          if (typeof navigator !== "undefined" && navigator.clipboard) {
            navigator.clipboard.writeText(item.upn).catch(() => {});
          }
          return true;
        },
      },
      ...(item.is_app_admin ? [
        { key: "divider", itemType: 1 as const },
        {
          key: "removeAdmin", text: "Remove admin", iconProps: { iconName: "Cancel" },
          onClick: () => { askRemoveAdmin(item); return true; },
        },
      ] : []),
    ],
    directionalHintFixed: true,
  }), [openEdit, askRemoveAdmin]);

  // Single 5-col table — User/Group, Type, Surfaces, Finance, Budget, Actions.
  const userColumns: IColumn[] = useMemo(
    () => [
      {
        key: "upn",
        name: "User / Group",
        minWidth: 220,
        maxWidth: 340,
        isResizable: true,
        onRender: (item: AccessUser) => {
          const isGroup = item.entity_type === "group";
          const display = item.display_name || item.upn;
          return (
            <div className={styles.identityCell}>
              <span className={styles.identityName}>
                {display}
                {item.is_app_admin && <span className={`${styles.chip} ${styles.chipAdmin}`}>ADMIN</span>}
                {isGroup && <span className={`${styles.chip} ${styles.chipGroup}`}>GROUP</span>}
              </span>
              {(item.display_name && !isGroup) || isGroup ? (
                <span className={styles.identitySub}>{item.upn}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        key: "type",
        name: "Type",
        minWidth: 64,
        maxWidth: 72,
        onRender: (item: AccessUser) => (
          <span className={`${styles.chip} ${item.entity_type === "group" ? styles.chipGroup : styles.chipUser}`}>
            {item.entity_type === "group" ? "Group" : "User"}
          </span>
        ),
      },
      {
        key: "surfaces",
        name: "Surfaces",
        minWidth: 100,
        maxWidth: 140,
        onRender: (item: AccessUser) => {
          if (item.is_app_admin) return <span style={{ color: "#1F6B2C", fontWeight: 500 }}>All</span>;
          if (!item.surfaces.length) return <span className={styles.muted}>none</span>;
          return (
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              <strong style={{ fontWeight: 500 }}>{item.surfaces.length}</strong>
              <span style={{ color: "#9A9AA3" }}>{" "}/ 19</span>
            </span>
          );
        },
      },
      {
        key: "finance",
        name: "Finance",
        minWidth: 130,
        maxWidth: 170,
        isResizable: true,
        onRender: (item: AccessUser) => {
          if (item.entity_type === "group") return <span className={styles.muted}>—</span>;
          const m = financeMode(item);
          const cls =
            m === "full" ? styles.pillFull : m === "restricted" ? styles.pillRestricted : styles.pillNone;
          const dirs = item.finance_directorates.length;
          const dpts = item.finance_departments.length;
          return (
            <div>
              <span className={`${styles.modeChip} ${cls}`}>
                {m === "full" ? "Full" : m === "restricted" ? "Restricted" : "None"}
              </span>
              {m === "restricted" && (
                <div className={styles.secondaryLine}>{dirs} dir · {dpts} dpt</div>
              )}
            </div>
          );
        },
      },
      {
        key: "budget",
        name: "Budget / mo",
        minWidth: 90,
        maxWidth: 110,
        onRender: (item: AccessUser) => {
          const v = item.monthly_limit_dollars;
          if (v === null || v === undefined || v === "") {
            return <span className={`${styles.budgetMono} ${styles.budgetUnlimited}`}>unlimited</span>;
          }
          const num = Number(v);
          if (Number.isNaN(num) || num === 0) {
            return <span className={`${styles.budgetMono} ${styles.budgetUnlimited}`}>unlimited</span>;
          }
          return <span className={styles.budgetMono}>${num.toFixed(2)}</span>;
        },
      },
      {
        key: "actions",
        name: "",
        minWidth: 80,
        maxWidth: 80,
        onRender: (item: AccessUser) => (
          <Stack horizontal className={styles.rowActions}>
            <IconButton
              iconProps={{ iconName: "Edit" }}
              title="Edit access"
              ariaLabel="Edit access"
              onClick={() => openEdit(item)}
            />
            <IconButton
              iconProps={{ iconName: "More" }}
              title="More actions"
              ariaLabel="More actions"
              menuProps={rowMenu(item)}
              menuIconProps={{ iconName: undefined }}
              styles={{ menuIcon: { display: "none" } }}
            />
          </Stack>
        ),
      },
    ],
    [openEdit, rowMenu]
  );

  if (loading) {
    return (
      <Stack horizontalAlign="center" styles={{ root: { padding: 40 } }}>
        <Spinner size={SpinnerSize.large} label="Loading access data…" />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 0 }}>
      {error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          {error}
        </MessageBar>
      )}


      {/* Phase polish PR1: 3 stacked MessageBars collapsed into one inline banner.
          Renders only when there's something to say. */}
      {(stale.directorates.length > 0 || stale.departments.length > 0 || !staleAvailable) && (
        <div className={styles.banner}>
          <span aria-hidden="true">⚠</span>
          {(stale.directorates.length > 0 || stale.departments.length > 0) && (
            <span>
              <strong>Heads up:</strong>{" "}
              {stale.directorates.length} directorate + {stale.departments.length} department grant(s)
              point at names that no longer exist in the live hierarchy. Inert until remapped.
            </span>
          )}
          {!staleAvailable && (
            <span><strong>Note:</strong> stale-grant detection unavailable — Fabric warehouse not reachable.</span>
          )}
          <div className={styles.bannerSpacer} />
        </div>
      )}

      <div className={styles.card}>
        <Text className={styles.cardTitle}>User access</Text>
        <Text className={styles.cardSub}>
          Manage every identity that touches the portal: users, security groups, and admins. Admins
          appear here too — marked with the <strong>ADMIN</strong> chip — and bypass scope filters.
          Granting a directorate covers all its departments; granting an individual department adds
          one outside any directorate grant.
        </Text>
        <div className={styles.counterStrip}>
          <span className={styles.counterStripItem}>
            <span className={styles.counterStripValue}>{cohortCounts.all}</span> identities
          </span>
          <span className={styles.counterStripItem}>
            <span className={styles.counterStripValue}>{cohortCounts.admin}</span> admins
          </span>
          <span className={styles.counterStripItem}>
            <span className={styles.counterStripValue}>{cohortCounts.group}</span> groups
          </span>
          <span className={styles.counterStripItem}>
            <span className={styles.counterStripValue}>{users.filter((u) => u.surfaces.length > 0).length}</span> with surfaces
          </span>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.pillGroup} role="tablist" aria-label="Filter by entity type">
            {(["all", "user", "admin", "group"] as const).map((c) => (
              <button
                key={c}
                type="button"
                className={`${styles.filterPill} ${cohort === c ? styles.filterPillActive : ""}`}
                onClick={() => setCohort(c)}
                aria-pressed={cohort === c}
              >
                {c === "all" ? "All" : c === "user" ? "Users" : c === "admin" ? "Admins" : "Groups"}
                <span className={`${styles.filterPillCount} ${cohort === c ? styles.filterPillCountActive : ""}`}>
                  {cohortCounts[c]}
                </span>
              </button>
            ))}
          </div>

          <SearchBox
            placeholder="Search by name or UPN…"
            value={searchTerm}
            onChange={(_, v) => setSearchTerm(v ?? "")}
            styles={{ root: { minWidth: 260, maxWidth: 320 } }}
          />

          <div className={styles.toolbarSpacer} />

          <PrimaryButton text="Add user" iconProps={{ iconName: "AddFriend" }} onClick={openAdd} />
          <DefaultButton text="Add security group" iconProps={{ iconName: "Group" }} onClick={openAddGroup} />
          <DefaultButton text="Add admin" iconProps={{ iconName: "Add" }} onClick={openAddAdmin} />
          <IconButton iconProps={{ iconName: "Refresh" }} title="Refresh" ariaLabel="Refresh" onClick={loadAll} />
        </div>

        <div className={styles.tableWrap}>
          <DetailsList
            items={filteredUsers}
            columns={userColumns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
          />
          {filteredUsers.length === 0 && (
            <div style={{ padding: 32, textAlign: "center", color: "#6B6B73" }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: "#161617", marginBottom: 4 }}>
                No {cohort === "all" ? "identities" : cohort + "s"} match
              </div>
              <div style={{ fontSize: 12 }}>Try a different filter or clear the search.</div>
            </div>
          )}
        </div>
      </div>

      <EditAccessPanel
        open={panelOpen}
        mode={panelMode}
        user={panelUser}
        domainCatalog={domainCatalog}
        onClose={() => setPanelOpen(false)}
        onSaved={loadAll}
      />

      <Dialog
        hidden={!removeAdminTarget}
        onDismiss={() => !removeAdminBusy && setRemoveAdminTarget(null)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Remove app admin?",
          subText: removeAdminTarget
            ? `${removeAdminTarget.display_name || removeAdminTarget.upn} will lose admin privileges. They keep their other access entries unless removed separately.`
            : "",
        }}
      >
        <DialogFooter>
          <PrimaryButton
            text={removeAdminBusy ? "Removing…" : "Remove admin"}
            onClick={confirmRemoveAdmin}
            disabled={removeAdminBusy}
            styles={{ root: { background: "#A4262C", borderColor: "#A4262C" }, rootHovered: { background: "#8E1F25", borderColor: "#8E1F25" } }}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => setRemoveAdminTarget(null)}
            disabled={removeAdminBusy}
          />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};

export default AdminAccessTab;
